import { useEffect, useMemo, useRef, useState } from "react";
import { Calculator as CalcIcon, DollarSign, TrendingUp, Receipt, Target, Percent, Sparkles, RotateCcw } from "lucide-react";
import { computeProfit, formatCurrency, getLastCalc, setLastCalc } from "../lib/storage";
import { toast } from "sonner";
const defaultInputs = {
  boothFee: "",
  travelCost: "",
  inventoryCost: "",
  suppliesCost: "",
  foodCost: "",
  marketingCost: "",
  otherCost: "",
  expectedSales: "",
  avgSale: "",
};

const costFields = [
  { key: "boothFee", label: "Booth fee" },
  { key: "travelCost", label: "Travel cost" },
  { key: "inventoryCost", label: "Inventory cost" },
  { key: "suppliesCost", label: "Supplies cost" },
  { key: "foodCost", label: "Food / drink cost" },
  { key: "marketingCost", label: "Marketing / signage" },
  { key: "otherCost", label: "Other costs" },
];

export default function Calculator() {
  const [inputs, setInputs] = useState(() => {
    const last = getLastCalc();
    return last ? { ...defaultInputs, ...last } : defaultInputs;
  });
  const hydrated = useRef(true);

  useEffect(() => {
    if (hydrated.current) setLastCalc(inputs);
  }, [inputs]);

  const result = useMemo(() => computeProfit(inputs), [inputs]);

  const update = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.value }));

  const reset = () => {
    setInputs(defaultInputs);
    toast.success("Calculator cleared");
  };

  const verdictStyles = {
    good: "bg-brand-success/10 text-brand-success border-brand-success/30",
    risky: "bg-brand-warning/10 text-brand-warning border-brand-warning/30",
    bad: "bg-brand-error/10 text-brand-error border-brand-error/30",
    neutral: "bg-brand-cream-soft text-brand-mute border-brand-line",
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 md:pb-16" data-testid="calculator-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="label-eyebrow">Profit Calculator</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink mt-2">
            Run the numbers
          </h1>
          <p className="text-brand-mute text-sm mt-1.5 max-w-lg">
            Enter your costs and expected sales. PopUp Profit does the math.
          </p>
        </div>
        <button onClick={reset} className="pop-btn-secondary" data-testid="calc-reset-btn">
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-3 space-y-6">
          {/* Costs */}
          <section className="pop-card p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-9 h-9 rounded-xl bg-brand-green/10 text-brand-green grid place-items-center">
                <Receipt className="w-5 h-5" strokeWidth={2} />
              </span>
              <div>
                <h2 className="font-heading text-lg font-semibold text-brand-ink">Costs</h2>
                <p className="text-xs text-brand-mute">What this event will cost you to show up.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {costFields.map((f) => (
                <CurrencyInput
                  key={f.key}
                  id={`calc-${f.key}`}
                  label={f.label}
                  value={inputs[f.key]}
                  onChange={update(f.key)}
                />
              ))}
            </div>
          </section>

          {/* Sales */}
          <section className="pop-card p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-9 h-9 rounded-xl bg-brand-gold/15 text-brand-gold grid place-items-center">
                <TrendingUp className="w-5 h-5" strokeWidth={2} />
              </span>
              <div>
                <h2 className="font-heading text-lg font-semibold text-brand-ink">Sales estimates</h2>
                <p className="text-xs text-brand-mute">Your honest best guess works fine.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="pop-label" htmlFor="calc-expectedSales">Expected number of sales</label>
                <input
                  id="calc-expectedSales"
                  data-testid="calc-expectedSales"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  placeholder="0"
                  value={inputs.expectedSales}
                  onChange={update("expectedSales")}
                  className="pop-input"
                />
              </div>
              <CurrencyInput
                id="calc-avgSale"
                label="Average sale amount"
                value={inputs.avgSale}
                onChange={update("avgSale")}
              />
            </div>
          </section>
        </div>

        {/* Results */}
        <aside className="lg:col-span-2 lg:sticky lg:top-24 self-start space-y-4" aria-label="Results">
          <div className="pop-card p-6 bg-brand-green text-white border-brand-green" data-testid="result-profit-card">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-white/70">Estimated profit</p>
            <p className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mt-2" data-testid="result-profit-value">
              {formatCurrency(result.profit)}
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-white/80">
              <Sparkles className="w-4 h-4 text-brand-gold" strokeWidth={2.25} />
              {result.verdict === "neutral" ? "Awaiting your inputs" : `${result.margin.toFixed(1)}% margin`}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ResultStat
              icon={DollarSign}
              label="Revenue"
              value={formatCurrency(result.revenue)}
              testid="result-revenue"
            />
            <ResultStat
              icon={Receipt}
              label="Total costs"
              value={formatCurrency(result.costs)}
              testid="result-costs"
            />
            <ResultStat
              icon={Target}
              label="Break-even sales"
              value={`${result.breakEvenSales}`}
              testid="result-breakeven"
            />
            <ResultStat
              icon={Percent}
              label="Profit margin"
              value={`${result.margin.toFixed(1)}%`}
              testid="result-margin"
            />
          </div>

          <div
            className={`rounded-2xl border p-5 ${verdictStyles[result.verdict]}`}
            data-testid="result-verdict"
            data-verdict={result.verdict}
          >
            <p className="text-xs uppercase tracking-[0.18em] font-semibold opacity-80">Recommendation</p>
            <p className="font-heading text-base sm:text-lg font-semibold mt-1.5 leading-snug">{result.message}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function CurrencyInput({ id, label, value, onChange }) {
  return (
    <div>
      <label className="pop-label" htmlFor={id}>{label}</label>
      <div className="relative">
        <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute pointer-events-none" strokeWidth={2} />
        <input
          id={id}
          data-testid={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={value}
          onChange={onChange}
          className="pop-input pl-11"
        />
      </div>
    </div>
  );
}

function ResultStat({ icon: Icon, label, value, testid }) {
  return (
    <div className="pop-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-brand-mute">
        <Icon className="w-4 h-4" strokeWidth={2} />
        <p className="text-[11px] uppercase tracking-wider font-semibold">{label}</p>
      </div>
      <p className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink mt-1.5 tracking-tight">{value}</p>
    </div>
  );
}
